import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, OnChanges, SimpleChanges, HostListener, WritableSignal, effect } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { ConfirmationService } from 'primeng/api';
import { interval, switchMap, of, Subscription, BehaviorSubject, Observable } from 'rxjs';
import { signal, computed, Signal } from '@angular/core';

@Component({
  selector: 'app-menu-page-detail',
  standalone: false,
  templateUrl: './menu-page-detail.component.html',
  styleUrls: ['./menu-page-detail.component.scss'],
  providers: [ConfirmationService]
})
export class MenuPageDetailComponent implements OnDestroy, OnChanges {

  @Input() model: WritableSignal<MenuItem[]> = signal([]);
  @Input() modeModification: boolean = false;
  @Input() hasUnsavedChanges: boolean = false;
  @Input() canEdit: boolean = true;

  @Output() onEdit = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<() => void>();
  @Output() onCancel = new EventEmitter<void>();

  @Input() autoSaveInterval: number = 0;
  @Input() autoSaveDelay: number = 0;

  @Output() onAutoSave = new EventEmitter<void>();

  isSaving: boolean = false;

  menuWithReturn: Signal<MenuItem[]> = computed(() => [
    {
      label: 'Retour',
      icon: 'pi pi-fw pi-angle-double-left',
      command: () => window.history.back()
    },
    ...this.model()
  ]);

  constructor(private confirmationService: ConfirmationService, private messageService: MessageService) {

    // Set up keyboard shortcuts
    this.setupKeyboardShortcuts();
    // Initialize autosave state based on current modeModification value
    this.updateAutoSaveState();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['modeModification'] || changes['autoSaveInterval']) {
      this.updateAutoSaveState(changes);
    }
  }

  ngOnDestroy() {
    // Clean up subscriptions
    if (this.autoSaveSubscription) {
      this.autoSaveSubscription.unsubscribe();
    }
  }

  private updateAutoSaveState(changes?: SimpleChanges) {
    if (this.modeModification && this.autoSaveInterval > 0) {
      // Start autosave when modeModification is on and interval is set
      const wasPaused = this.pauserAutoSave.value;
      const intervalChanged = changes?.['autoSaveInterval'] && !changes['autoSaveInterval'].isFirstChange;
      
      // If already running and interval changed, restart by pausing then unpausing
      if (!wasPaused && intervalChanged) {
        this.pauserAutoSave.next(true);
        // Use setTimeout to ensure pause completes before unpause
        setTimeout(() => {
          this.pauserAutoSave.next(false);
        }, 0);
      } else {
        // Otherwise, just set the state
        this.pauserAutoSave.next(false);
      }
    } else {
      // Pause autosave when modeModification is off or interval is 0
      this.pauserAutoSave.next(true);
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (this.modeModification) {
      // Ctrl+S for save
      if (event.ctrlKey && event.key === 's') {
        event.preventDefault();
        this.handleSave();
      }
      // Escape for cancel
      if (event.key === 'Escape') {
        this.confirmCancel();
      }
    }
  }

  private setupKeyboardShortcuts() {
    // Additional keyboard shortcut setup if needed
  }

  confirmCancel() {
    if (this.hasUnsavedChanges) {
      this.confirmationService.confirm({
        message: 'Êtes-vous sûr de vouloir annuler ? Toutes les modifications non sauvegardées seront perdues.',
        header: 'Confirmation d\'annulation',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.onCancel.emit();
        }
      });
    } else {
      this.onCancel.emit();
    }
  }

  // Method to handle save with loading state
  handleSave() {
    this.isSaving = true;
    var callback = () => this.isSaving = false;
    this.onSave.emit(callback);
  }

  isMobile() {
    return window.innerWidth < 768;
  }

  countdown: Observable<number> = new Observable<number>(observer => {
    let count = 10;
    const interval = setInterval(() => {
        observer.next(count);
        count--;
        if (count < 0) {
            observer.complete();
            clearInterval(interval);
        }
    }, 1000);

    return () => {
        clearInterval(interval);
    };
});

  pauserAutoSave: BehaviorSubject<boolean> = new BehaviorSubject(true);
  autoSaveSubscription: Subscription = this.pauserAutoSave.pipe(
    switchMap(isPaused => {
      if (isPaused) {
        return of();
      } else {
        return interval(this.autoSaveInterval);
      }
    })
  ).subscribe(() => {
    if (this.autoSaveDelay > 0) {
      this.messageService.add({ key: 'autoSaveToast', severity: 'info', summary: 'Sauvegarde automatique', detail: 'Sauvegarde automatique dans ' + this.autoSaveDelay / 1000 + ' secondes', life: this.autoSaveDelay });
    }
    setTimeout(() => {
      this.onAutoSave.emit();
    }, this.autoSaveDelay);
  });
}
